import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase, isAuthConfigured } from "@/lib/supabase";
import { trackSignIn } from "@/lib/analytics";

interface AuthContextType {
  // The signed-in user, or null when anonymous / auth not configured.
  user: User | null;
  session: Session | null;
  // True until the initial session check resolves (avoids a sign-in-button
  // flicker on load).
  loading: boolean;
  // Whether auth is wired up at all (env vars present). When false the
  // sign-in UI hides and the app stays anonymous.
  isConfigured: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  // If auth isn't configured we're never "loading" a session.
  const [loading, setLoading] = useState<boolean>(isAuthConfigured);

  useEffect(() => {
    if (!supabase) return;

    // Initial session (from persisted storage), then subscribe to changes.
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next);
      setUser(next?.user ?? null);
      setLoading(false);
      // Funnel: count a genuine sign-in (the auth conversion). SIGNED_IN
      // also fires on every session restore (page reload while logged in),
      // so guard with a per-browser-session flag to avoid over-counting
      // returning reloads as new sign-ins.
      if (event === "SIGNED_IN") {
        try {
          if (!sessionStorage.getItem("sa_signin_tracked")) {
            sessionStorage.setItem("sa_signin_tracked", "1");
            trackSignIn();
          }
        } catch {
          trackSignIn();
        }
      }
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        // Return to the page the user is on after the Google round-trip.
        redirectTo: window.location.origin + window.location.pathname,
      },
    });
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isConfigured: isAuthConfigured,
        signInWithGoogle,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
